import { Fragment, useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { ArrowLeft, Download, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { RankList } from '../components/RankList';
import { RateCell, pctText } from '../components/RateCell';
import { Tag } from '../components/Tag';
import { loadOnlineConversationRaw } from '../api';
import { cleanTranscript } from '../chatClean';
import type { OnlineConvRow, OnlineRawConversationResponse, OnlineRawMessage, OnlineResponse, OnlineSummaryStruct, OnlineTargetRow } from '../types';
import { num } from '../utils';
import { RANGE_OPTIONS } from '../ranges';
import { TARGET_CHILDREN, TARGET_COLOR, TARGET_LABEL, TARGET_PARENT_OF, TARGET_PARENTS } from '../targetGroups';

interface OnlineScreenProps {
  data: OnlineResponse | null;
  loaded: boolean;
  busy: boolean;
  filters: OnlineFilters;            // kept for signature parity (cross-filter state is internal)
  setFilters: (filters: OnlineFilters) => void;
  range: string;
  onRange: (range: string) => void;
  serverFilters: Record<string, string[]>;
  onServerFilters: (filters: Record<string, string[]>) => void;
}
export interface OnlineFilters { channel: string; intent: string; priority: string; }

type Gran = 'day' | 'week' | 'month';
type Dim = 'channels' | 'targets' | 'needTypes' | 'subcats' | 'intents' | 'stages' | 'blockers' | 'priorities' | 'groups' | 'agents';
type CFilters = Record<Dim, string[]>;
const EMPTY: CFilters = {
  channels: [], targets: [], needTypes: [], subcats: [], intents: [], stages: [], blockers: [], priorities: [], groups: [], agents: [],
};
const ALL_DIMS = Object.keys(EMPTY) as Dim[];
const DETAIL_DIMS: Dim[] = ['needTypes', 'subcats', 'intents', 'stages', 'blockers', 'priorities', 'groups', 'agents'];

// ---- time bucketing ----
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
function periodLabel(code: string): string {
  if (/^\d{4}-W\d{2}$/.test(code)) return `T${code.slice(6)}/${code.slice(2, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(code)) return `${code.slice(8)}/${code.slice(5, 7)}`;
  if (/^\d{4}-\d{2}$/.test(code)) return `Th${code.slice(5)}/${code.slice(2, 4)}`;
  return code;
}

const BLOCKER_LABEL: Record<string, string> = {
  out_of_stock: 'Hết hàng', demo_unavailable: 'Thiếu demo', price_budget: 'Giá / ngân sách',
  customer_left_early: 'Khách về sớm', comparing_shops: 'So sánh nơi khác', just_browsing: 'Chỉ dạo xem',
  technical_issue: 'Vướng kỹ thuật', not_in_business: 'Không kinh doanh', internal_approval: 'Chờ duyệt nội bộ',
  waiting_response: 'Chờ phản hồi', missing_contact: 'Thiếu liên hệ', no_blocker: 'Không vướng', unknown: 'Chưa xác định',
};
const STAGE_LABEL: Record<string, string> = {
  new_consulting: 'Mới tư vấn', followup_needed: 'Cần chăm tiếp', stock_blocked: 'Vướng hàng',
  demo_blocked: 'Vướng demo', deposit_pending: 'Đã cọc', closed_won: 'Đã chốt (AI)',
  service_recheck: 'Dịch vụ', lost_unknown: 'Mất dấu',
};
const PRIO_LABEL: Record<string, string> = { p0: 'P0 · gấp', p1: 'P1 · cao', p2: 'P2 · theo dõi', p3: 'P3 · thấp' };
const INTENT_LABEL: Record<string, string> = { high: 'Cao · sẵn mua', medium: 'Trung bình', low: 'Tham khảo', service: 'Dịch vụ' };
const NEED_LABEL: Record<string, string> = { sales: 'Mua hàng', service: 'Dịch vụ', missing_info: 'Thiếu thông tin' };
const NEED_OPTIONS = ['sales', 'service'];
// CareSoft names the Facebook channel "Inbox Facebook"; the quick bar shows the short name.
const CHANNEL_SHORT: Record<string, string> = { 'Inbox Facebook': 'Facebook' };

function labelFor(dim: Dim, code: string): string {
  if (dim === 'targets') return TARGET_LABEL[code] || code;
  if (dim === 'blockers') return BLOCKER_LABEL[code] || code;
  if (dim === 'stages') return STAGE_LABEL[code] || code;
  if (dim === 'priorities') return PRIO_LABEL[code] || code;
  if (dim === 'intents') return INTENT_LABEL[code] || code;
  if (dim === 'needTypes') return NEED_LABEL[code] || code;
  return code;
}
const DIM_TITLE: Record<Dim, string> = {
  channels: 'Kênh', targets: 'Ngành target', needTypes: 'Nhu cầu', subcats: 'Nhóm hàng chi tiết',
  intents: 'Ý định mua', stages: 'Giai đoạn', blockers: 'Lý do chưa mua', priorities: 'Ưu tiên',
  groups: 'Nhóm', agents: 'Nhân viên',
};

function entries(rows: Array<Record<string, number | string | null>> | undefined, key: string, value: string): Array<[string, number]> {
  const m = new Map<string, number>();
  (rows || []).forEach(row => {
    const k = String(row[key] || '');
    if (!k) return;
    m.set(k, (m.get(k) || 0) + Number(row[value] || 0));
  });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function targetEntriesOf(rows: OnlineTargetRow[] | undefined): Array<[string, number]> {
  const m = new Map<string, number>();
  (rows || []).forEach(row => { if (row.target) m.set(row.target, (m.get(row.target) || 0) + Number(row.conversations || 0)); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

const expandTarget = (code: string): string[] => TARGET_CHILDREN[code] || [code];
const sameSet = (a: string[], b: string[]) => a.length === b.length && b.every(v => a.includes(v));

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

interface RawConversationState {
  open: boolean;
  loading: boolean;
  data?: OnlineRawConversationResponse;
  error?: string;
}

export function OnlineScreen({ data, loaded, busy, range, onRange, serverFilters, onServerFilters }: OnlineScreenProps) {
  const [filters, setFilters] = useState<CFilters>({ ...EMPTY, ...serverFilters });
  const [history, setHistory] = useState<CFilters[]>([]);
  const [gran, setGran] = useState<Gran>('day');
  const [query, setQuery] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [detailLimit, setDetailLimit] = useState(100);
  const [rawConversations, setRawConversations] = useState<Record<string, RawConversationState>>({});
  const rows = data?.conversations || [];
  const detailTotal = Number(data?.meta?.detail_total || rows.length);

  const commit = (next: CFilters) => {
    setHistory(h => [...h.slice(-19), filters]);
    setFilters(next);
    setDetailLimit(100);
    onServerFilters(next);
  };
  const toggle = (dim: Dim, value: string) => {
    if (!value) return;
    const cur = filters[dim];
    commit({ ...filters, [dim]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] });
  };
  const setOne = (dim: Dim, value: string) => commit({ ...filters, [dim]: value ? [value] : [] });
  const isTarget = (code: string) => sameSet(filters.targets, expandTarget(code));
  const setTarget = (code: string) => commit({ ...filters, targets: !code || isTarget(code) ? [] : expandTarget(code) });
  const goBack = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFilters(prev);
    setHistory(history.slice(0, -1));
    setDetailLimit(100);
    onServerFilters(prev);
  };
  const clearAll = () => { commit(EMPTY); setQuery(''); };
  const setGranularity = (g: Gran) => setGran(g);
  const changeRange = (v: string) => { setHistory([]); setFilters(EMPTY); setQuery(''); onRange(v); };
  const activeCount = ALL_DIMS.reduce((n, d) => n + filters[d].length, 0) + (query.trim() ? 1 : 0);
  const detailCount = DETAIL_DIMS.reduce((n, d) => n + filters[d].length, 0);

  const loadRawConversation = async (conversationId: string) => {
    setRawConversations(prev => ({ ...prev, [conversationId]: { open: true, loading: true } }));
    try {
      const raw = await loadOnlineConversationRaw(conversationId);
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: { open: prev[conversationId]?.open ?? true, loading: false, data: raw },
      }));
    } catch (error) {
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: {
          open: prev[conversationId]?.open ?? true,
          loading: false,
          error: String((error as Error)?.message || error),
        },
      }));
    }
  };

  const toggleRawConversation = async (conversationId: string) => {
    const current = rawConversations[conversationId];
    if (current?.open) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: false } }));
      return;
    }
    if (current?.data) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: true } }));
      return;
    }
    if (current?.loading) return;
    await loadRawConversation(conversationId);
  };

  const total = Number(data?.dashboard?.conversations ?? rows.length);
  const warm = Number(data?.dashboard?.warm_leads || 0);
  const highValue = Number(data?.dashboard?.high_value || 0);
  const urgent = Number(data?.dashboard?.urgent_followup || 0);
  const phoneKnown = Number(data?.dashboard?.phone_known || 0);
  // ai_deal_stage=closed_won is a weak AI label (1.4% of ONLINE chats). Show the count only,
  // never as a close rate.
  const aiClosed = Number(data?.dashboard?.closed_won || 0);
  const warmRate = total ? warm / total : null;

  const periodEntries = useMemo(() => {
    const m = new Map<string, { n: number; warm: number }>();
    (data?.periods || []).forEach(row => {
      const day = String(row.period || '');
      if (!day) return;
      const k = gran === 'day' ? day : gran === 'month' ? day.slice(0, 7) : isoWeek(day);
      const e = m.get(k) || { n: 0, warm: 0 };
      e.n += Number(row.n || 0);
      e.warm += Number(row.warm || 0);
      m.set(k, e);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [data?.periods, gran]);

  const channelEntries = entries(data?.channels, 'channel', 'conversations');
  const targetEntries = useMemo(() => targetEntriesOf(data?.targets), [data?.targets]);
  const intentEntries = entries(data?.intents, 'ai_purchase_intent', 'n');
  const stageEntries = entries(data?.deal_stages, 'ai_deal_stage', 'n');
  const blockerEntries = entries(data?.blockers, 'ai_blocker_type', 'n');
  const priorityEntries = entries(data?.priorities, 'ai_followup_priority', 'n');
  const subcatEntries = entries(data?.subcats, 'ai_subcategory_name', 'n');

  const groupEntries = useMemo(() => {
    const m = new Map<string, number>();
    (data?.groups || []).forEach(g => {
      const k = String(g.group_name || '');
      if (k) m.set(k, (m.get(k) || 0) + Number(g.conversations || 0));
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [data?.groups]);
  const agentEntries = useMemo(() => {
    const m = new Map<string, number>();
    (data?.groups || []).forEach(g => {
      const k = String(g.agent_name || '');
      if (k) m.set(k, (m.get(k) || 0) + Number(g.conversations || 0));
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [data?.groups]);

  const groupRows = useMemo(() => (data?.groups || []).map(g => ({
    group: String(g.group_name || ''),
    agent: String(g.agent_name || ''),
    n: Number(g.conversations || 0),
    warm: Number(g.warm_leads || 0),
    urgent: Number(g.urgent_followup || 0),
    complaint: Number(g.complaints || 0),
  })).filter(g => g.group || g.agent), [data?.groups]);

  const products = useMemo(() => (data?.products || []).map(p => ({
    name: String(p.product_label || p.product || ''),
    count: Number(p.mentions || 0),
    warm: Number(p.warm_leads || 0),
  })).filter(p => p.name), [data?.products]);

  const needle = query.trim().toLowerCase();
  const displayed = useMemo(() => {
    if (!needle) return rows;
    return rows.filter(r => {
      const s = r.ai_summary_struct;
      return [
        r.day, r.channel, r.agent_name, r.group_name, r.ai_category, r.ai_subcategory_name,
        r.ai_purchase_intent ? labelFor('intents', r.ai_purchase_intent) : '',
        s?.headline, s?.customer_need, s?.sale_response, s?.outcome, s?.next_step,
        r.ai_need_summary,
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
  }, [rows, needle]);

  const fullParents = TARGET_PARENTS.map(p => p.code).filter(code => (TARGET_CHILDREN[code] || []).length > 1
    && TARGET_CHILDREN[code].every(c => filters.targets.includes(c)));
  const inPeri = filters.targets.length > 0 && filters.targets.every(c => TARGET_PARENT_OF[c] === 'PED');
  const targetCrumb = fullParents.length === 1 ? fullParents[0] : filters.targets.length === 1 ? filters.targets[0] : '';
  const channelLabel = filters.channels.length ? filters.channels.join(', ') : 'Tất cả kênh';
  const needCrumb = filters.needTypes.length === 1 ? labelFor('needTypes', filters.needTypes[0]) : '';

  const latestVn = data?.latest?.max_start_vn ? data.latest.max_start_vn.replace(/^(\d{4})-(\d{2})-(\d{2}) /, '$3/$2 ') : '—';

  const exportCsv = () => {
    const head = ['ngay', 'kenh', 'nhom', 'nhan_vien', 'nganh_target', 'nhom_hang', 'y_dinh', 'giai_doan', 'ly_do', 'uu_tien', 'tieu_de', 'khach_can', 'sale_da_lam', 'ket_qua', 'buoc_tiep', 'noi_dung_goc'];
    const lines = displayed.map(r => {
      const s = r.ai_summary_struct;
      return [
        r.day, r.channel, r.group_name, r.agent_name, labelFor('targets', r.target || ''),
        r.ai_subcategory_name, r.ai_purchase_intent ? labelFor('intents', r.ai_purchase_intent) : '',
        r.ai_deal_stage ? labelFor('stages', r.ai_deal_stage) : '',
        r.ai_blocker_type ? labelFor('blockers', r.ai_blocker_type) : '',
        r.ai_followup_priority ? labelFor('priorities', r.ai_followup_priority) : '',
        s?.headline, s?.customer_need, s?.sale_response, s?.outcome, s?.next_step, r.ai_need_summary,
      ].map(csvCell).join(',');
    });
    const blob = new Blob([`﻿${[head.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tu-van-online-${range}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const selectOf = (dim: Dim, options: Array<[string, number]>, all: string) => {
    const cur = filters[dim];
    return (
      <select className={`ins-select ${cur.length ? 'has-value' : ''}`} aria-label={DIM_TITLE[dim]} value={cur.length === 1 ? cur[0] : cur.length ? '__multi' : ''}
        onChange={e => e.target.value !== '__multi' && setOne(dim, e.target.value)}>
        <option value="">{all}</option>
        {cur.length > 1 && <option value="__multi">{DIM_TITLE[dim]}: {cur.length} đã chọn</option>}
        {options.map(([k, n]) => <option key={k} value={k}>{labelFor(dim, k)} · {num(n)}</option>)}
      </select>
    );
  };

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu tư vấn online...</div>;
  const empty = loaded && total === 0;

  const channelOptions = channelEntries.map(e => e[0]);

  return (
    <>
      <div className="ins-sticky">
        <nav className={`ins-scopebar ${activeCount ? 'is-filtered' : ''}`} aria-label="Bộ lọc">
          <div className="ins-row">
            <div className="ins-crumbs">
              <b>{channelLabel}</b>
              {targetCrumb && <span>› {labelFor('targets', targetCrumb)}</span>}
              {needCrumb && <span>› {needCrumb}</span>}
              <em>{num(total)} hội thoại · {num(rows.length)} dòng chi tiết</em>
              {detailChipsFor(filters).map(({ dim, val }) => (
                <button key={`${dim}:${val}`} type="button" className="ins-chip" onClick={() => toggle(dim, val)} title="Bỏ lọc này">
                  <span>{DIM_TITLE[dim]}:</span> {labelFor(dim, val)} <X size={12} />
                </button>
              ))}
              <small className="muted">· chat tới {latestVn}</small>
            </div>
            <div className="ins-actions">
              <select className="ins-range" aria-label="Khoảng thời gian" value={range} disabled={busy} onChange={e => changeRange(e.target.value)}>
                {RANGE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <label className={`ins-search ${query ? 'has-value' : ''}`} title="Tìm trong tóm tắt AI và hội thoại đã tải">
                <Search size={14} />
                <input type="search" value={query} placeholder="Tìm sản phẩm, NV, nội dung…" onChange={e => { setQuery(e.target.value); setDetailLimit(100); }} />
              </label>
              <button type="button" className={`ins-detail-toggle ${moreOpen ? 'is-on' : ''} ${detailCount ? 'has-value' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen(v => !v)} title="Lọc chi tiết: nhóm hàng, ý định, giai đoạn, lý do, ưu tiên, nhóm/nhân viên">
                <SlidersHorizontal size={15} /><span>Lọc chi tiết</span>{detailCount > 0 && <b>{detailCount}</b>}
              </button>
              <button type="button" className="ins-iconbtn" onClick={goBack} disabled={!history.length} title="Quay lại bộ lọc trước" aria-label="Quay lại"><ArrowLeft size={15} /></button>
              <button type="button" className="ins-clear" onClick={clearAll} disabled={!activeCount} title="Xóa tất cả lọc"><RotateCcw size={15} /><span>Xóa lọc</span></button>
              <button type="button" className="ins-iconbtn" onClick={exportCsv} disabled={!displayed.length} title="Xuất CSV theo bộ lọc (đã ẩn PII)" aria-label="Xuất CSV"><Download size={15} /></button>
            </div>
          </div>
          <div className="ins-quickbar" role="group" aria-label="Lọc nhanh">
            <div className="ins-seg" role="group" aria-label="Kênh">
              <button type="button" aria-pressed={!filters.channels.length} onClick={() => setOne('channels', '')}>Tất cả</button>
              {channelOptions.map(ch => <button key={ch} type="button" aria-pressed={filters.channels.includes(ch)} onClick={() => toggle('channels', ch)}>{CHANNEL_SHORT[ch] || ch}</button>)}
            </div>
            {inPeri ? (
              // Drilled into PERI: the target row becomes its split, like the CFO breadcrumb.
              <div className="ins-seg" role="group" aria-label="Nhóm PERI">
                <button type="button" onClick={() => setTarget('')} title="Về mọi ngành"><ArrowLeft size={13} /> Mọi ngành</button>
                <button type="button" aria-pressed={isTarget('PED')} onClick={() => setTarget(isTarget('PED') ? '' : 'PED')}>PERI</button>
                {TARGET_CHILDREN.PED.map(c => <button key={c} type="button" aria-pressed={sameSet(filters.targets, [c])} onClick={() => setTarget(sameSet(filters.targets, [c]) ? 'PED' : c)}>{labelFor('targets', c)}</button>)}
              </div>
            ) : (
              <div className="ins-seg" role="group" aria-label="Ngành target">
                <button type="button" aria-pressed={!filters.targets.length} onClick={() => setTarget('')}>Mọi ngành</button>
                {TARGET_PARENTS.map(p => <button key={p.code} type="button" aria-pressed={isTarget(p.code)} onClick={() => setTarget(p.code)} title={p.label}>{p.code === 'OTHER' ? 'Khác' : p.label}</button>)}
              </div>
            )}
          </div>
          {moreOpen && (
            <div className="ins-drawer">
              {selectOf('needTypes', NEED_OPTIONS.map(v => [v, Number((data?.needs || []).find(n => n.ai_need_type === v)?.n || 0)] as [string, number]), 'Mọi nhu cầu')}
              {selectOf('subcats', subcatEntries, 'Mọi nhóm hàng chi tiết')}
              {selectOf('intents', intentEntries, 'Mọi ý định mua')}
              {selectOf('stages', stageEntries, 'Mọi giai đoạn')}
              {selectOf('blockers', blockerEntries, 'Mọi lý do chưa mua')}
              {selectOf('priorities', priorityEntries, 'Mọi mức ưu tiên')}
              {selectOf('groups', groupEntries, 'Mọi nhóm')}
              {selectOf('agents', agentEntries, 'Mọi nhân viên')}
            </div>
          )}
        </nav>
      </div>

      {empty && <div className="card empty space">Chưa có hội thoại online trong khoảng này. Dữ liệu online về theo lô 1 lần/ngày (khoảng 15h–20h) nên "Hôm nay" thường trống tới chiều — chọn "Hôm qua" hoặc "7 ngày".</div>}

      {!empty && (<>
        <section id="online-overview" className="grid kpis ins-anchor">
          <KpiCard label="Hội thoại" value={num(total)} sub={`${num(rows.length)} dòng đã tải / ${num(detailTotal)} · Zalo/FB/Livechat`} tone="blue" />
          <KpiCard label="Lead ấm" value={pctText(warmRate)} sub={`${num(warm)} hội thoại có ý định mua cao hoặc trung bình`} tone="green" />
          <KpiCard label="Cần ưu tiên" value={num(urgent)} sub="Mức gấp hoặc cao" tone="amber" />
          <KpiCard label="Đã chốt (AI)" value={num(aiClosed)} sub="Nhãn AI, chỉ để tham khảo — không phải tỷ lệ chốt" tone="teal" />
          <KpiCard label="Biết SĐT" value={num(phoneKnown)} sub="Hội thoại khách để lại số" tone="cyan" />
          <KpiCard label="Khách giá trị cao" value={num(highValue)} sub="Theo nhu cầu và ngành hàng" tone="violet" />
        </section>

        <section className="card space">
          <div className="section-title">
            <h2>Xu hướng hội thoại &amp; lead ấm</h2>
            <div className="ins-seg small">
              {(['day', 'week', 'month'] as Gran[]).map(g => (
                <button key={g} type="button" aria-pressed={gran === g} onClick={() => setGranularity(g)}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>
              ))}
            </div>
          </div>
          <div className="chart">
            <ChartBox
              labels={periodEntries.map(e => periodLabel(e[0]))}
              datasets={[
                { label: 'Hội thoại', data: periodEntries.map(e => e[1].n), type: 'bar', backgroundColor: '#2563eb99', borderColor: '#2563eb', borderWidth: 1, borderRadius: 5, maxBarThickness: 48 } as unknown as ChartDataset,
                { label: 'Lead ấm', data: periodEntries.map(e => e[1].warm), type: 'line', borderColor: '#16a34a', backgroundColor: '#16a34a22', borderWidth: 2, tension: 0.35, pointRadius: 2, spanGaps: false, datalabels: { display: false } } as unknown as ChartDataset,
              ]}
            />
          </div>
        </section>

        <section className="grid split space atop">
          <div className="card">
            <div className="section-title"><h2>Ngành target</h2><span>PERI tách theo nhóm · bấm để lọc</span></div>
            <RankList entries={targetEntries} selected={filters.targets} onToggle={value => toggle('targets', value)} labelFor={value => labelFor('targets', value)} color="var(--blue)" limit={12} />
          </div>
          <div className="card">
            <div className="section-title"><h2>Kênh</h2><span>bấm để lọc</span></div>
            <RankList entries={channelEntries} selected={filters.channels} onToggle={value => toggle('channels', value)} labelFor={value => value} color="var(--cyan)" limit={10} />
          </div>
        </section>

        <section className="grid three space atop">
          <div className="card">
            <div className="section-title"><h2>Ý định mua</h2><span>bấm để lọc</span></div>
            <RankList entries={intentEntries} selected={filters.intents} onToggle={value => toggle('intents', value)} labelFor={value => labelFor('intents', value)} color="var(--green)" limit={8} />
          </div>
          <div className="card">
            <div className="section-title"><h2>Giai đoạn tư vấn</h2><span>bấm để lọc</span></div>
            <RankList entries={stageEntries} selected={filters.stages} onToggle={value => toggle('stages', value)} labelFor={value => labelFor('stages', value)} color="var(--violet)" limit={8} />
          </div>
          <div className="card">
            <div className="section-title"><h2>Lý do khách chưa mua</h2><span>bấm để lọc</span></div>
            <RankList entries={blockerEntries} selected={filters.blockers} onToggle={value => toggle('blockers', value)} labelFor={value => labelFor('blockers', value)} color="var(--amber)" limit={8} />
          </div>
        </section>

        <section className="grid split space atop">
          <div className="card">
            <div className="section-title"><h2>Nhóm hàng chi tiết</h2><span>bấm để lọc</span></div>
            <RankList entries={subcatEntries} selected={filters.subcats} onToggle={value => toggle('subcats', value)} labelFor={value => value} color="var(--teal)" limit={12} />
          </div>
          <div className="card">
            <div className="section-title"><h2>Ưu tiên chăm sóc</h2><span>bấm để lọc</span></div>
            <RankList entries={priorityEntries} selected={filters.priorities} onToggle={value => toggle('priorities', value)} labelFor={value => labelFor('priorities', value)} color="var(--indigo)" limit={8} />
          </div>
        </section>

        <section id="online-staff" className="card space ins-anchor">
          <div className="section-title"><h2>Năng suất theo nhóm / nhân viên</h2><span>{num(groupRows.length)} dòng · tỷ lệ ấm tô màu theo ngưỡng · bấm để lọc</span></div>
          <div className="ins-band-legend">
            <span className="b-good">≥ 50%</span><span className="b-ok">30–50%</span><span className="b-warn">15–30%</span><span className="b-bad">&lt; 15%</span>
          </div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table className="ins-crtable">
              <thead><tr><th>Nhóm</th><th>Nhân viên</th><th className="right">Hội thoại</th><th className="right">Lead ấm</th><th>Tỷ lệ ấm</th><th className="right">Gấp</th></tr></thead>
              <tbody>
                {groupRows.length === 0 ? (
                  <tr><td colSpan={6} className="muted">Không có dữ liệu theo bộ lọc hiện tại.</td></tr>
                ) : groupRows.slice(0, 100).map((g, i) => {
                  const groupOn = filters.groups.includes(g.group);
                  const agentOn = filters.agents.includes(g.agent);
                  return (
                    <tr key={`${g.group}-${g.agent}-${i}`} className={groupOn || agentOn ? 'is-selected' : ''}>
                      <td>{g.group ? <button type="button" className="ins-link" aria-pressed={groupOn} onClick={() => toggle('groups', g.group)}>{g.group}</button> : <span className="muted">—</span>}</td>
                      <td>{g.agent ? <button type="button" className="ins-link" aria-pressed={agentOn} onClick={() => toggle('agents', g.agent)}><b>{g.agent}</b></button> : <span className="muted">—</span>}</td>
                      <td className="right tnum"><b>{num(g.n)}</b></td>
                      <td className="right tnum">{num(g.warm)}</td>
                      <td>
                        <RateCell o={{ n: g.n, sales: g.n, won: g.warm, lost: 0 }} active={groupOn || agentOn}
                          onClick={() => toggle(g.agent ? 'agents' : 'groups', g.agent || g.group)}
                          title={`${num(g.warm)} lead ấm / ${num(g.n)} hội thoại · ${num(g.urgent)} cần gấp`} />
                      </td>
                      <td className="right tnum">{g.urgent ? <Tag tone="amber">{num(g.urgent)}</Tag> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card space">
          <div className="section-title">
            <h2>Chi tiết hội thoại</h2>
            <span>{num(displayed.length)} / {num(rows.length)} dòng đã tải{needle ? ' khớp tìm kiếm' : ''} · SĐT đã ẩn</span>
          </div>
          <div className="scroll">
            <table className="ins-detail">
              <thead><tr><th>Ngày</th><th>Kênh</th><th>NV</th><th>Ngành target</th><th>Nhóm hàng</th><th>Ý định</th><th>Ưu tiên</th></tr></thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={7} className="muted">{needle ? 'Không có hội thoại khớp tìm kiếm trong dữ liệu đã tải.' : 'Không có hội thoại theo bộ lọc hiện tại.'}</td></tr>
                ) : displayed.slice(0, detailLimit).map(r => {
                  const raw = rawConversations[r.conversation_id];
                  const rawPanelId = `online-raw-${r.conversation_id.replace(/[^A-Za-z0-9_-]/g, '-')}`;
                  // Two rows per conversation: the facts, then the summary across the full width so it reads
                  // like a note instead of being squeezed into the last column.
                  return (
                    <Fragment key={r.conversation_id}>
                    <tr className="online-meta-row">
                      <td className="tnum nowrap muted">{r.day || '—'}</td>
                      <td><b>{r.channel || '—'}</b></td>
                      <td>{r.agent_name || '—'}</td>
                      <td><span className="ins-stage"><i style={{ background: TARGET_COLOR[r.target || 'OTHER'] || 'var(--muted)' }} />{labelFor('targets', r.target || 'OTHER')}</span></td>
                      <td className="muted">{r.ai_subcategory_name || '—'}</td>
                      <td>{r.ai_purchase_intent ? <Tag tone={r.ai_purchase_intent === 'high' ? 'green' : r.ai_purchase_intent === 'medium' ? 'amber' : r.ai_purchase_intent === 'service' ? 'blue' : 'gray'}>{labelFor('intents', r.ai_purchase_intent)}</Tag> : '—'}</td>
                      <td>{r.ai_followup_priority ? <Tag tone={r.ai_followup_priority === 'p0' ? 'red' : r.ai_followup_priority === 'p1' ? 'amber' : 'gray'}>{labelFor('priorities', r.ai_followup_priority)}</Tag> : '—'}</td>
                    </tr>
                    <tr className="online-summary-row">
                      <td colSpan={7} className="online-detail-cell">
                        <ConversationContent row={r} />
                        <button type="button" className="online-raw-link" aria-expanded={Boolean(raw?.open)} aria-controls={rawPanelId}
                          onClick={() => { void toggleRawConversation(r.conversation_id); }}>
                          {raw?.open ? 'Ẩn hội thoại gốc' : 'Xem hội thoại gốc'}
                        </button>
                        {raw?.open && (
                          <div className="online-raw-panel" id={rawPanelId}>
                            {raw.loading && <span className="muted">Đang tải hội thoại gốc...</span>}
                            {raw.error && (
                              <div className="online-raw-error">
                                <span>{raw.error}</span>
                                <button type="button" onClick={() => { void loadRawConversation(r.conversation_id); }}>Thử lại</button>
                              </div>
                            )}
                            {raw.data && <RawConversation data={raw.data} />}
                          </div>
                        )}
                      </td>
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {displayed.length > detailLimit && (
            <button type="button" className="ins-more" onClick={() => setDetailLimit(n => n + 200)}>
              Xem thêm {num(Math.min(200, displayed.length - detailLimit))} dòng · còn {num(displayed.length - detailLimit)}
            </button>
          )}
        </section>

        {products.length > 0 && (
          <section className="card space">
            <div className="section-title"><h2>Sản phẩm khách hỏi — tối ưu lineup</h2><span>{num(products.length)} sản phẩm · theo bộ lọc hiện tại</span></div>
            <div className="scroll" style={{ maxHeight: 360 }}>
              <table>
                <thead><tr><th>Sản phẩm</th><th className="right">Lượt hỏi</th><th className="right">Lead ấm</th></tr></thead>
                <tbody>{products.slice(0, 40).map((p, i) => (
                  <tr key={`${p.name}-${i}`}>
                    <td>{p.name}</td>
                    <td className="right tnum"><b>{num(p.count)}</b></td>
                    <td className="right tnum">{p.warm ? <Tag tone="green">{num(p.warm)}</Tag> : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        )}
      </>)}
    </>
  );
}

function detailChipsFor(filters: CFilters): Array<{ dim: Dim; val: string }> {
  return DETAIL_DIMS.flatMap(dim => filters[dim].map(val => ({ dim, val })));
}

function ConversationContent({ row }: { row: OnlineConvRow }) {
  const s = row.ai_summary_struct;
  if (s && (s.headline || s.customer_need || s.sale_response || s.outcome || s.next_step || s.empty)) {
    return <SummaryStructView s={s} />;
  }
  return <TranscriptPreview text={row.ai_need_summary} />;
}

function SummaryStructView({ s }: { s: OnlineSummaryStruct }) {
  const fields: Array<[string, string | null | undefined]> = [
    ['Khách cần', s.customer_need],
    ['Sale đã làm', s.sale_response],
    ['Kết quả', s.outcome],
    ['Bước tiếp', s.next_step],
  ];
  return (
    <div className="online-summary-card">
      {s.headline && <b className="online-summary-headline">{s.headline}</b>}
      {s.empty && <span className="muted">Không có nội dung phía khách.</span>}
      <div className="online-summary-fields">
        {fields.filter(([, value]) => value && String(value).trim()).map(([label, value]) => (
          <div key={label}><span>{label}</span><p>{value}</p></div>
        ))}
      </div>
    </div>
  );
}

function TranscriptPreview({ text }: { text?: string | null }) {
  const turns = useMemo(() => cleanTranscript(text).slice(0, 8), [text]);
  if (!turns.length) return <span className="muted">Chưa có tóm tắt cho hội thoại này.</span>;
  return (
    <div className="online-turns">
      {turns.map((turn, i) => (
        <div key={`${turn.who}-${i}`} className={`online-turn ${turn.who}`}>
          <b>{turn.who === 'sale' ? 'NV' : 'KH'}</b>
          <span>{turn.text}</span>
        </div>
      ))}
    </div>
  );
}

function RawConversation({ data }: { data: OnlineRawConversationResponse }) {
  return (
    <div className="online-raw-transcript">
      <div className="online-raw-summary">
        <span>{num(data.returned_messages)} / {num(data.total_messages)} tin nhắn</span>
        {data.truncated && <b>Chỉ hiển thị 500 tin đầu tiên</b>}
      </div>
      {data.messages.length === 0 && <span className="muted">Hội thoại chưa có tin nhắn raw.</span>}
      {data.messages.map(message => <RawMessageView key={message.message_id} message={message} />)}
    </div>
  );
}

function RawMessageView({ message }: { message: OnlineRawMessage }) {
  const roleLabel = message.role === 'agent'
    ? 'NV'
    : message.role === 'visitor' ? 'Khách' : message.role === 'system' ? 'Hệ thống' : 'Không rõ';
  return (
    <div className={`online-raw-message ${message.role}`}>
      <div className="online-raw-meta">
        <b>{roleLabel}{message.speaker ? ` · ${message.speaker}` : ''}</b>
        <span>{message.event_time || 'Không có thời gian'} · type {message.message_type}</span>
      </div>
      <div className="online-raw-text">{message.text || '—'}</div>
    </div>
  );
}
